import { TeamAndDetails } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/company';
import { ContractDocumentType } from '@thera-hr/api-sdk/api/resources/model/resources/thera';
import { Employee } from '@thera-hr/check-api-sdk/api';
import { IPillStatus } from 'components/app-components/ui/status-pill';

import { CompanyType } from 'context/company';
import { StripeOnboardingStatus } from 'lib/constants';
import { EQUITYADMIN_COMPANYID, PROD_DEMO_ACC_COMPANYID, SCALE_ARMY_COMPANYID } from 'lib/whitelistConstants';
import { IContractSigningDocument, IEmployeeContracts, IEmployment, IUser } from 'types';
import { ICheckCompany } from 'types/check';
import { isDateInPast } from './dateTime';
import { isStytchProdEnv } from './user';

export const contractStatusToDisplay = {
  STRIPE_LINK_COMPLETED: 'Onboarded',
  STRIPE_ACCOUNT_MADE: 'Onboarding in progress',
  INVITED: 'Onboarding in progress',
  INACTIVE: 'Inactive',
  CONTRACT_NOT_SIGNED: 'Contract not signed',
  ONBOARDED: 'Onboarded',
};

// type StatusType = keyof typeof contractStatusToDisplay;
export type IContractStatus = ReturnType<typeof checkWorkerContractSigningStatus>;
type IOnboardingStatus = StripeOnboardingStatus & {
  ONBOARDED: 'ONBOARDED';
};

export type IEmployeeStatus = {
  active?: boolean;
  onboarded?: boolean;
  employment_status?: 'live' | 'leave' | 'terminated';
  onboardingStatus?: string;
  contractSigned?: boolean;
};

export const checkIfContractActive = (endDate: string): boolean => {
  if (!endDate) return true;
  return !isDateInPast(endDate);
};
export const isContractActive = checkIfContractActive;

/** Checks if the given contract is valid BEFORE raising invoices and/or making payments */
export const checkWorkerContractSigningStatus = (contract: IEmployment) => {
  let contractSigned = false;

  // if the contract is active, and the compliance information is present/valid
  // if (contract?.contractDocumentsRequired && !complianceInformation?.documents?.length) {
  //   complianceDocsUploaded = false;
  // }
  // if the contract was set not required or for previous versions, it is valid
  if (!contract?.contractDocumentType || contract?.contractDocumentType === 'NO_CONTRACT') {
    contractSigned = true;
  }
  // if the contract is a contract wizard, it is valid if it has a contractFileID (i.e. it has been signed)
  if (
    (contract?.contractDocumentType === 'CONTRACT_WIZARD' || contract?.contractDocumentType === 'CUSTOM_CONTRACT') &&
    contract?.contractFileID
  ) {
    contractSigned = true;
  }
  // if the contract is user provided, it is valid if it has a contract file ID (i.e. it has been uploaded)
  if (contract?.contractDocumentType === 'USER_PROVIDED' && contract?.contractFileID) {
    contractSigned = true;
  }

  return contractSigned;
};

/**
 * Only contracts Thera generates go out for e-signature, so only they have a company signer.
 * Uploaded contracts and no-contract hires never do, and the backend drops any signer sent for them.
 */
export const isSignedOnThera = (contractDocumentType?: ContractDocumentType | null): boolean =>
  contractDocumentType === 'CONTRACT_WIZARD' || contractDocumentType === 'CUSTOM_CONTRACT';

/**
 * Extracts per-party signing status from PandaDoc document recipients.
 * Returns false for both if no document/recipients data is available.
 */
export const getDocumentSigningStatus = (
  document?: IContractSigningDocument,
): { contractorSigned: boolean; companySigned: boolean } => {
  if (!document?.recipients) return { contractorSigned: false, companySigned: false };
  const contractorSigned = Boolean(document.recipients.find((r: any) => r.role === 'CONTRACTOR')?.hasCompleted);
  const companySigned = Boolean(document.recipients.find((r: any) => r.role === 'COMPANY')?.hasCompleted);
  return { contractorSigned, companySigned };
};

/**
 * Single source of truth for whether a W2 employee still needs to sign their employment document.
 * `isSignedByEmployee` flips as soon as the employee's own signature is done, independent of the
 * company's countersignature — mirrors the contractor "unsigned contract" check.
 */
export const getW2ContractSignStatus = (
  contract: IEmployment,
  document?: IContractSigningDocument,
): { requiresSignature: boolean; isSignedByEmployee: boolean; isFullyExecuted: boolean } => {
  const requiresSignature =
    contract?.contractDocumentType === 'CONTRACT_WIZARD' || contract?.contractDocumentType === 'CUSTOM_CONTRACT';
  const { contractorSigned: isSignedByEmployee } = getDocumentSigningStatus(document);
  const isFullyExecuted =
    requiresSignature && Boolean((contract as { contractFileId?: string })?.contractFileId || contract?.contractFileID);

  return { requiresSignature, isSignedByEmployee, isFullyExecuted };
};

/**
 * Returns contract signing status info (text, color, pill status).
 * When document recipients data is available, provides granular status
 * distinguishing between "Company Signature Pending" and "Contractor Signature Pending".
 */
export const getContractStatusInfo = (
  contract: IEmployment,
  document?: IContractSigningDocument,
): { text: string; color: 'green' | 'amber' | 'neutral'; pillStatus: IPillStatus } => {
  const isActive = checkIfContractActive(contract?.terminationDetails?.endDate);
  if (!isActive) return { text: 'Inactive', color: 'neutral', pillStatus: 'neutral' };

  const isSigned = checkWorkerContractSigningStatus(contract);
  if (isSigned) return { text: 'Active', color: 'green', pillStatus: 'ready' };

  const isSignRequired =
    contract?.contractDocumentType === 'CONTRACT_WIZARD' || contract?.contractDocumentType === 'CUSTOM_CONTRACT';

  if (isSignRequired && document?.recipients) {
    const { contractorSigned, companySigned } = getDocumentSigningStatus(document);
    if (contractorSigned && !companySigned) {
      return { text: 'Company Signature Pending', color: 'amber', pillStatus: 'inProgress' };
    }
    if (!contractorSigned && companySigned) {
      return { text: 'Contractor Signature Pending', color: 'amber', pillStatus: 'inProgress' };
    }
  }

  return { text: 'Signature Pending', color: 'amber', pillStatus: 'inProgress' };
};

/**
 * Returns the appropriate fallback text for the contract document field when contractFileID is null.
 * Replaces misleading "No contract uploaded" with context-aware messaging.
 */
export const getContractDocumentFallbackText = (contract: IEmployment, document?: IContractSigningDocument): string => {
  const isSignRequired =
    contract?.contractDocumentType === 'CONTRACT_WIZARD' || contract?.contractDocumentType === 'CUSTOM_CONTRACT';

  if (!isSignRequired) return 'No contract provided';

  if (document?.recipients) {
    const { contractorSigned, companySigned } = getDocumentSigningStatus(document);
    if (contractorSigned && !companySigned) return 'Company Signature Pending';
    if (!contractorSigned && companySigned) return 'Contractor Signature Pending';
  }

  return 'Signature Pending';
};

export const TheraContractStatus = (contractStatus: IEmployeeStatus): string => {
  if (!contractStatus?.active) return 'Inactive';
  if (!contractStatus?.onboarded) return contractStatusToDisplay[contractStatus?.onboardingStatus];
  if (!contractStatus?.contractSigned) return 'Contract not signed';
};

export const TheraOnboardingStatus = (
  employeeInformation: IUser['employeeInformation'],
): {
  onboarded: boolean;
  onboardingStatus: string;
  statusText?: string;
} => {
  let isOnboarded = false;
  const { onboardingStatus: _onboardingStatus, address } = employeeInformation || {};
  const defaultOnboardingStatus = 'INVITED';
  let onboardingStatus = (_onboardingStatus || defaultOnboardingStatus) as IOnboardingStatus;

  // if the address is present, the user is onboarded
  if (address?.line1) {
    isOnboarded = true;
    onboardingStatus = 'ONBOARDED' as IOnboardingStatus;
  }
  return {
    onboarded: isOnboarded,
    onboardingStatus,
    statusText: contractStatusToDisplay[onboardingStatus],
  };
};

export const getTeamIDsToFilter = ({
  showAllTeamsContracts,
  teams,
  currentTeamId,
}: {
  showAllTeamsContracts: boolean;
  teams: Record<string, TeamAndDetails>;
  currentTeamId: string;
}) => {
  if (showAllTeamsContracts) return null;
  const teamIds = Object.keys(teams || {});
  return currentTeamId ? [currentTeamId] : teamIds;
};

/**
 * Flattens the filtered teams into the set of contract IDs they contain, for scoping
 * contract-carrying records (invoices, expenses, time off) by team in one Set lookup
 * per record instead of a per-record scan over every team. Null means no team filter.
 */
export const getTeamContractIdSet = ({
  teams,
  teamIdsToFilter,
}: {
  teams: Record<string, TeamAndDetails>;
  teamIdsToFilter: string[] | null;
}): Set<string> | null => {
  if (!teamIdsToFilter?.length) return null;
  return new Set(teamIdsToFilter.flatMap((teamId) => teams?.[teamId]?.contractIds ?? []));
};

export const getCurrentEmployeeContract = (employeesAndEmployment: IEmployeeContracts[], contractID: string) => {
  if (!employeesAndEmployment?.length || !contractID) return;
  const employeeContract = employeesAndEmployment
    ?.flatMap((employeeContracts) => employeeContracts.employments)
    .find((contract) => contract.id === contractID);

  return employeeContract;
};

/**
 * Returns the status of the worker profile - based on the contracts and onboarding status
 * Active - Any Active Contract, but no Pending Signature Contracts
 * Pending Signature - Any Pending Signature Contracts (that is if the contract's contractDocumentType is not NO_CONTRACT and contractFileID is null)
 * Onboarding - While the contractor is onboarding for the first time
 * Inactive - No Active Contracts
 */
export const getWorkerProfileStatus = (
  userWithContracts: IEmployeeContracts,
  eorTrackerData?: any,
): {
  text: string;
  type: IPillStatus;
} => {
  const { employments = [], user } = userWithContracts;

  const activeContracts = employments?.filter((contract) => !isDateInPast(contract?.terminationDetails?.endDate));
  const inactiveContracts = employments?.filter((contract) => isDateInPast(contract?.terminationDetails?.endDate));
  const pendingSignatureContracts = activeContracts?.filter(
    (contract) =>
      contract.contractDocumentType !== 'NO_CONTRACT' && !contract.contractFileID && contract.type === 'CONTRACTOR',
  );

  // If no contracts exist, do not return 'Inactive'
  if (employments.length === 0) {
    return { text: 'No Contracts', type: 'neutral' };
  }

  if (inactiveContracts?.length === employments?.length) {
    return { text: 'Inactive', type: 'neutral' };
  }

  // Check if this is an EOR employee
  const hasEOREmployment = employments?.some((emp) => emp.type === 'EMPLOYEE');
  let isEOROnboarding = false;

  // For EOR employees, check the tracker data
  if (hasEOREmployment && eorTrackerData) {
    let matchingEOREmployee = null;

    if (eorTrackerData.employees?.length) {
      matchingEOREmployee = eorTrackerData.employees.find((eorEmp) => eorEmp.email === user.email);
    } else if (eorTrackerData.employee) {
      if (eorTrackerData.employee.email === user.email) {
        matchingEOREmployee = eorTrackerData.employee;
      }
    }

    if (matchingEOREmployee?.Status === 'Onboarding In Progress') {
      isEOROnboarding = true;
    }
  }

  if (!user?.employeeInformation?.address?.line1 || isEOROnboarding) {
    return {
      text: 'Onboarding',
      type: 'inProgress',
    };
  }

  if (pendingSignatureContracts?.length) {
    return {
      text: 'Signature Pending',
      type: 'inProgress',
    };
  }

  if (activeContracts?.length) {
    return { text: 'Active', type: 'ready' };
  }

  return { text: 'Active', type: 'ready' };
};

/**
 * Returns the status of the check W2 employee profile - based on the his employment status
 * Active - if employee.employment_status === 'live' and employee.onboarded === true
 * Onboarding - if employee.onboarded === false
 * Inactive - if employee.employment_status === 'leave' or 'terminated'
 */
export const getCheckEmployeeStatus = (
  employee: Employee,
): {
  text: string;
  type: IPillStatus;
} => {
  const { terminationDate, onboard, active } = employee || {};
  const isTerminated = isDateInPast(terminationDate);
  if (isTerminated) {
    return {
      text: 'Terminated',
      type: 'warning',
    };
  }
  if (!active) {
    return {
      text: 'Inactive',
      type: 'neutral',
    };
  }
  if (onboard?.status === 'blocking') {
    return {
      text: 'Blocking',
      type: 'warning',
    };
  }
  if (onboard?.status !== 'completed') {
    return {
      text: 'Onboarding',
      type: 'inProgress',
    };
  }
  return {
    text: 'Active',
    type: 'ready',
  };
};

// The display-facing bucket the user sees in the pill and the "Onboarding" table filter.
// A Check employee whose only block is `employee_details` is conceptually still in
// onboarding (the admin needs to finish their profile), so we surface it as Onboarding
// rather than Blocking. Keep all count/filter/display sites going through this helper so
// they can't diverge.
export const getCheckEmployeeDisplayStatus = (
  employee: Employee,
): {
  text: string;
  type: IPillStatus;
} => {
  const raw = getCheckEmployeeStatus(employee);
  if (raw.text === 'Terminated' || raw.text === 'Inactive') {
    return raw;
  }
  if (employee?.onboard?.blockingSteps?.[0] === 'employee_details') {
    return { text: 'Onboarding', type: 'inProgress' };
  }
  return raw;
};

export const getNonTerminatedEmployees = (employees: Employee[]) => {
  return employees?.filter((employee) => {
    if (employee.terminationDate) {
      return !isDateInPast(employee.terminationDate);
    }
    return true;
  });
};

export const isCompanyImplementationDone = (company: ICheckCompany) => {
  const { implementation } = company || {};
  if (implementation?.status !== 'completed') {
    return false;
  }
  return true;
};

export const getContractTypeLabel = (contractType: IEmployment['type']) => {
  if (contractType === 'CONTRACTOR') {
    return 'Contractor';
  } else if (contractType === 'EMPLOYEE') {
    return 'Employee (EOR)';
  } else {
    return 'Employee';
  }
};

export const isRecentRoute = (initialRouteTimestamp: string) => {
  const currentTimestamp = new Date().getTime();
  const timeDifference = currentTimestamp - parseInt(initialRouteTimestamp);
  const _isRecentRoute = timeDifference < 1000 * 60 * 5; // 5 minutes
  return _isRecentRoute;
};

export const isContractRenewalAllowed = (companyID: string) => {
  return (
    companyID === EQUITYADMIN_COMPANYID ||
    companyID === SCALE_ARMY_COMPANYID ||
    companyID === PROD_DEMO_ACC_COMPANYID ||
    !isStytchProdEnv
  );
};

export const numberOfTeamsWithAutoPay = (
  teamsAutoPayInfo: CompanyType['teamsById'],
  teams: { [k: string]: TeamAndDetails },
) => {
  const _numberOfTeamsWithAutoPay = Object.keys(teams).filter(
    (teamID) =>
      teamsAutoPayInfo[teamID]?.autoPay?.enabled &&
      teamsAutoPayInfo[teamID]?.autoPay?.disabledReason !== 'INSUFFICIENT_FUNDS',
  );
  return _numberOfTeamsWithAutoPay?.length;
};

export const numberOfTeamsWithAutoPayPaused = (
  teamsAutoPayInfo: CompanyType['teamsById'],
  teams: { [k: string]: TeamAndDetails },
) => {
  return Object.keys(teams).filter(
    (teamID) => teamsAutoPayInfo[teamID]?.autoPay?.disabledReason === 'INSUFFICIENT_FUNDS',
  ).length;
};

export const numberOfTeamsWithAutoApproveFixedRateInvoices = (
  teamsInfo: CompanyType['teamsById'],
  teams: { [k: string]: TeamAndDetails },
) => {
  const _numberOfTeamsWithAutoApprove = Object.keys(teams).filter((teamID) =>
    teamsInfo[teamID]?.autoApproveInvoice === null ? true : teamsInfo[teamID]?.autoApproveInvoice || false,
  );
  return _numberOfTeamsWithAutoApprove?.length;
};

export const numberOfTeamsWithInvoiceAvailabilityOverride = (
  teamsInfo: CompanyType['teamsById'],
  teams: { [k: string]: TeamAndDetails },
) => {
  return Object.keys(teams).filter(
    (teamID) =>
      teamsInfo?.[teamID]?.paymentAllowedDaysBeforeDueDate !== undefined &&
      teamsInfo?.[teamID]?.paymentAllowedDaysBeforeDueDate !== null,
  ).length;
};
